import type { ArticleBody } from './types';

const NHS = (path: string, label: string) => ({ label: `NHS, ${label}`, href: `https://www.nhs.uk/${path}` });
const AAP = (path: string, label: string) => ({ label: `American Academy of Pediatrics (HealthyChildren.org) ${label}`, href: `https://www.healthychildren.org/English/${path}` });

export const SITUATIONS: Record<string, ArticleBody> = {
  'late-talking': {
    intro: [
      'Grandparents, neighbours and even doctors may say “he’ll talk when he’s ready” and some late talkers do catch up. But others don’t, and it is impossible to know which from the outside. Checking costs little; waiting too long can mean missing the years when help works best.',
    ],
    notice: [
      'By 18 months: fewer than about 10 words, and not pointing to show you things.',
      'By 2 years: not putting two words together (“more milk”, “papa gone”).',
      'Understanding seems behind too, not following simple requests without gestures.',
      'Any loss of words they used to say.',
    ],
    helps: [
      'Talk through your day together in short, simple sentences.',
      'Offer choices you hold up: “Banana or apple?” and wait for any attempt.',
      'Copy their sounds and gestures, then add a word.',
      'Keep the TV and phone off in the background during play and meals.',
    ],
    when: ['Any of the signs above, a loss of words, or a gut feeling that won’t go away. Ask for a hearing test as well.'],
    who: ['Speech and language therapist', 'Audiologist', 'Developmental paediatrician'],
    nurture: 'The home assessment takes about 15–20 minutes and shows where talking and understanding sit for your child’s age, a clear starting point for that conversation with a professional.',
    sources: [AAP('ages-stages/toddler/Pages/Language-Delay.aspx', 'Language delay'), { label: 'ASHA; Speech and language development', href: 'https://www.asha.org/public/speech/development/' }],
  },

  'tantrums-meltdowns': {
    intro: [
      'A **tantrum** is usually about wanting something, and it often eases when the child sees it won’t work. A **meltdown** is different: the child is overwhelmed (by noise, tiredness, change, or feelings) and has lost control. Meltdowns are not a choice, and they need calm, not consequences.',
    ],
    notice: [
      'What happened just before? Hunger, tiredness, a “no”, turning off a screen, a change of plan, a noisy place.',
      'How big, and how long? Is it getting better or worse over the weeks?',
      'What helped it end? A quiet space, a hug, waiting, distraction?',
    ],
    helps: [
      'In the moment: stay close and calm, say little, keep them safe, and move them somewhere quieter if you can.',
      'Don’t reason or bargain during a meltdown; talk about it later, when everyone is calm.',
      'Warn before transitions (“Five more minutes, then shoes on”) and use a timer they can see.',
      'Keep meals and sleep regular; hunger and tiredness are behind many hard moments.',
      'Notice and praise calm moments, and teach one simple calming skill when they’re settled.',
    ],
    when: ['Outbursts are very frequent, very long, or dangerous to your child or others.', 'They continue well past age 5 without easing, or come with other concerns about development.'],
    who: ['Child psychologist', 'Developmental paediatrician', 'Occupational therapist (if sensory triggers)'],
    nurture: 'Use **Log a moment** in the app to note hard moments with what came before and what helped. After a few weeks, the pattern is easy to show a professional.',
    sources: [NHS('conditions/baby/babys-development/behaviour/temper-tantrums/', 'Temper tantrums'), AAP('family-life/family-dynamics/communication-discipline/Pages/default.aspx', 'Communication and discipline')],
  },

  'picky-eating': {
    intro: [
      'Most young children go through phases of eating only a few foods. For some, often children with sensory differences or autism, the list stays very short, and new foods can cause real distress. The goal is to widen the list slowly, without battles.',
    ],
    notice: ['How many foods do they reliably eat? Fewer than about 20 is worth mentioning to a professional.', 'Is it texture, colour, smell, temperature or brand that matters?', 'Are they growing and gaining weight as expected?'],
    helps: [
      'Serve one “safe” food at every meal, alongside a tiny portion of something new, with no pressure to eat it.',
      'Let them touch, smell, lick or help cook new foods; that all counts as progress.',
      'Change safe foods by tiny steps (a different shape of the same roti, then a slightly different flour).',
      'Eat together, keep mealtimes short and pleasant, and avoid bribes or force.',
    ],
    when: ['Weight loss or poor growth, gagging or vomiting with foods, or the list of foods is shrinking.'],
    who: ['Paediatrician', 'Occupational therapist (feeding)', 'Dietitian'],
    nurture: 'The home programme includes mealtime activities in the “mealtime” room, designed to build comfort with food step by step.',
    sources: [NHS('conditions/baby/weaning-and-feeding/fussy-eaters/', 'Fussy eaters')],
  },

  'sleep-troubles': {
    intro: [
      'Sleep problems are common in all children and especially common in children with autism, ADHD or anxiety. Poor sleep makes everything harder the next day, for your child and for you.',
    ],
    notice: ['How long it takes to fall asleep, how often they wake, and what time they wake up.', 'Snoring, gasping or pauses in breathing at night; mention these to a doctor.', 'Screens, sugar or play just before bed.'],
    helps: [
      'Keep the same bedtime and wake-up time every day, including weekends and holidays.',
      'Build a calm, short routine in the same order: bath, pyjamas, brush teeth, one story, lights out.',
      'No screens for at least an hour before bed; keep the room dark, cool and quiet.',
      'Get outdoor daylight and physical activity every day.',
      'If they wake, keep it boring: calm, dark and brief.',
    ],
    when: ['Snoring with pauses in breathing, very frequent waking, or sleep problems that affect daytime behaviour or learning.'],
    who: ['Paediatrician', 'Developmental paediatrician'],
    nurture: 'Log how nights are going with **Log a moment**; the pattern over a few weeks is useful to share at your next visit.',
    sources: [AAP('healthy-living/sleep/Pages/default.aspx', 'Sleep')],
  },

  'toilet-training': {
    intro: [
      'Children are ready for toilet training at very different ages. Many are ready between 2 and 3 years; children with developmental delays or autism often need longer and a more step-by-step approach. Readiness matters more than age.',
    ],
    notice: ['Staying dry for an hour or two at a time.', 'Showing they know when they are wet or soiled.', 'Being able to sit for a short while and follow a simple instruction.'],
    helps: [
      'Use a picture sequence of the steps on the bathroom wall.',
      'Take them at regular times (after waking, after meals, before going out) rather than asking “do you need to go?”',
      'Keep it calm and positive; never punish accidents.',
      'Use the same words and routine every time, at home and with other caregivers.',
    ],
    when: ['No progress after several months of trying, pain, constipation, or a return to wetting after being dry.'],
    who: ['Paediatrician', 'Occupational therapist'],
    nurture: 'The independence part of the home assessment covers toileting and self-care for your child’s age, and the report suggests a next step.',
    sources: [NHS('conditions/baby/babys-development/potty-training-and-bedwetting/how-to-potty-train/', 'How to potty train'), AAP('ages-stages/toddler/toilet-training/Pages/default.aspx', 'Toilet training')],
  },

  'screen-time': {
    intro: [
      'Screens are part of family life. What matters most is what screens replace, talking, play, sleep and moving, and whether an adult is watching and talking with the child.',
      'The World Health Organization recommends no sedentary screen time for children under 2, and no more than 1 hour a day for 2- to 4-year-olds, less is better.',
    ],
    helps: [
      'Watch together and talk about what you see.',
      'Keep screens out of meals, bedrooms and the hour before bed.',
      'Replace screen time with something specific (“After lunch we play with blocks”) rather than just taking it away.',
      'Use a visual timer and warn before it ends. Expect protest the first week. It passes.',
      'Notice your own screen use around your child.',
    ],
    myths: [{ myth: 'Educational videos teach toddlers to talk.', fact: 'Young children learn language mainly from real back-and-forth with people, not from screens.' }],
    when: ['Screen time is causing serious conflict, sleep problems, or your child has no interest in anything else.'],
    who: ['Paediatrician', 'Child psychologist'],
    nurture: 'The home programme gives you a short, screen-free activity for each day, placed where it naturally happens at home.',
    sources: [{ label: 'World Health Organization; Guidelines on physical activity, sedentary behaviour and sleep for children under 5', href: 'https://www.who.int/news/item/24-04-2019-to-grow-up-healthy-children-need-to-sit-less-and-play-more' }, AAP('family-life/Media/Pages/default.aspx', 'Media')],
  },

  'school-refusal': {
    intro: [
      'Some children become very upset about going to school: tummy aches on Monday mornings, tears at the gate, or refusing to get ready. It is usually a sign that something at school feels too hard: learning, friendships, noise, a teacher, bullying, or separation from you.',
    ],
    notice: ['Physical complaints that disappear on weekends and holidays.', 'Worry that builds on Sunday evenings or after holidays.', 'Changes in mood, sleep or eating around school.'],
    helps: [
      'Listen first. Ask what the hardest part of the day is, and when it feels easier.',
      'Keep going to school if you possibly can, even for part of the day, because staying home tends to make it harder to go back.',
      'Meet the class teacher to find out what they see and agree a plan together.',
      'Plan a calm morning routine and a clear, short goodbye.',
    ],
    when: ['Your child has missed school for more than a few days, or you suspect bullying, a learning difficulty, anxiety or low mood.'],
    who: ['School counsellor', 'Child psychologist', 'Special educator'],
    nurture: 'A home assessment can show whether attention, learning-related skills or mood are worth looking at alongside the school.',
    sources: [NHS('mental-health/children-and-young-adults/', 'Children and young people’s mental health')],
  },

  'teenage-mood': {
    intro: [
      'Teenagers go through a lot: bodies changing, exams, friendships, identity, social media. Moodiness and wanting more privacy are normal. But low mood, worry or withdrawal that lasts for weeks and gets in the way of life is worth taking seriously.',
    ],
    notice: [
      'Sad, irritable or flat most days for two weeks or more.',
      'Pulling away from friends and things they used to enjoy.',
      'Big changes in sleep, eating, or school performance.',
      'Talking about feeling hopeless, being a burden, or hurting themselves.',
    ],
    helps: [
      'Find low-pressure times to talk, in the car, on a walk, while cooking, side by side is easier than face to face.',
      'Listen more than you advise. “That sounds really hard” goes a long way.',
      'Keep some routines together: a meal, a walk, a show you watch together.',
      'Encourage sleep, movement and time with friends.',
    ],
    when: [
      'Any talk of self-harm or suicide: call **Tele-MANAS 14416** (free, 24×7) straight away, or your local emergency number.',
      'Low mood or worry lasting more than two weeks, or affecting school, friendships or sleep.',
    ],
    who: ['Child and adolescent psychiatrist', 'Clinical psychologist', 'School counsellor'],
    nurture: 'Home assessments for 12- to 18-year-olds include questions on mood and wellbeing. If an answer suggests your teenager may be at risk, you will see crisis-line details straight away.',
    sources: [{ label: 'World Health Organization; Mental health of adolescents', href: 'https://www.who.int/news-room/fact-sheets/detail/adolescent-mental-health' }, AAP('ages-stages/teen/Pages/default.aspx', 'Teens'), { label: 'Tele-MANAS (Government of India)', href: 'https://telemanas.mohfw.gov.in/' }],
  },
};
